import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { CocacolaTag, DecisionPoint, LogoBackground } from '../../assets/images';
import {
  CocacolaTagImage,
  HeaderLogoContainer,
  LoginBackgroundImage,
  LoginPageContainer,
  LoginSection,
  RightPanel,
  LoginTitle,
  LoginSubtitle,
  CenteredGrid,
  CommonButton,
} from './Login.Styled';
import { Grid, Typography } from '@mui/material';
import { graphConfig, loginScopes } from '../../features/auth/msalConfig';
import { getCookie } from '../../utils/helper';
import { setIsLoginProgress } from '../../features/auth/authSlice';
import { CONSTANTS } from '../../utils/constant';
import { CompanyLogo } from '../../assets/images/header';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, inProgress, accounts } = useMsal();
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const scopes = loginScopes.scopes;

  const loginClick = async () => {
    try {
      setIsLoginProgress(true);
      const account = accounts[0];

      if (!account) {
        const loginResponse = await instance.loginPopup({ scopes });
        const updatedAccounts = instance.getAllAccounts();
        if (updatedAccounts.length > 0) {
          handleSuccessfulLogin(updatedAccounts[0]);
        }
      } else {
        const tokenResponse: AuthenticationResult = await instance.acquireTokenPopup({ scopes });
        handleTokenResponse(tokenResponse);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleTokenResponse = (tokenResponse: AuthenticationResult) => {
    try {
      const accessToken = tokenResponse.accessToken;
      const expires = tokenResponse.expiresOn || new Date();
      const username = tokenResponse.account.name || '';

      document.cookie = `accessToken=${accessToken}; expires=${expires.toUTCString()}; path=/`;
      document.cookie = `username=${username}; expires=${expires.toUTCString()}; path=/`;
      setUser(tokenResponse.account?.idTokenClaims || null);
      setIsLoginProgress(false);
      navigate(`/${CONSTANTS.PATH.LANDING}`);
    } catch (error) {
      setError('An error occurred while processing the token.');
    }
  };

  const handleSuccessfulLogin = (account: any) => {
    instance
      .acquireTokenSilent({ scopes, account })
      .then(handleTokenResponse)
      .catch((error) => {
        handleError(error);
      });
  };

  const handleError = (error: any) => {
    if (error instanceof InteractionRequiredAuthError) {
      setError('Interaction required for authentication. Please try again.');
    } else {
      setError('An unexpected error occurred. Please try again.');
    }
  };

  const validateToken = async () => {
    const token = getCookie('accessToken');
    if (!token) {
      if (location.pathname !== '/login') {
        navigate(`/${CONSTANTS.PATH.LOGIN}`);
      }
      return;
    }

    try {
      setIsLoginProgress(true);
      const response = await fetch(graphConfig.graphMeEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        if (location.pathname === '/login' || !getCookie('username')) {
          setIsLoginProgress(false);
          navigate(`/${CONSTANTS.PATH.LANDING}`);
        }
      } else {
        setError('Invalid token. Please log in again.');
      }
    } catch (error) {
      setError('An error occurred while validating the token. Please log in again.');
    }
  };

  useEffect(() => {
    if (inProgress === 'none') {
      validateToken();
    }
  }, [inProgress, accounts]);

  const moreInfo = CONSTANTS.MORE_INFO;
  const loginHeader = CONSTANTS.HEADER_LABEL;

  return (
    <Grid style={{ height: '98vh' }}>
      <LoginPageContainer>
        <Grid container sx={{ height: '100%', boxShadow: 1 }}>
          <RightPanel>
            <HeaderLogoContainer>
              <LoginBackgroundImage src={LogoBackground} alt="Logo" />
              <CocacolaTagImage src={CocacolaTag} alt="Logo" />
            </HeaderLogoContainer>
            <LoginSection>
              <CenteredGrid item xs={12}>
                <LoginTitle>{loginHeader}</LoginTitle>
                <LoginSubtitle>Welcome to our one-stop RGM Pricing Tool</LoginSubtitle>
                <CommonButton variant="contained" onClick={loginClick}>
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: 24,
                      lineHeight: '29.26px',
                      textTransform: 'none',
                      letterSpacing: '2%',
                    }}
                  >
                    Enter RGM Playground
                  </Typography>
                </CommonButton>
              </CenteredGrid>
            </LoginSection>
          </RightPanel>
          <Grid style={{ position: 'absolute', right: '4%', bottom: '4%' }}>
            <img src={DecisionPoint} alt="info" />
          </Grid>
        </Grid>
      </LoginPageContainer>
    </Grid>
  );
};

export default Login;
